import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useCallback, useRef, } from 'react';
import { Tabs } from 'antd';
import { EditOutlined, PlusOutlined, SearchOutlined, } from '@ant-design/icons';
import { txt } from 'libs/i18n';
import { useSession } from 'components/Authenticated';
import SelectTab from './SelectTab';
import CreateTab from './CreateTab';
import EditTab from './EditTab';
import ReplaceModeRadio from '../ReplaceModeRadio';
import Citation from '../Citation';
import { ReplaceMode } from '../types';
import styles from './style.module.less';
const getInitialReplaceMode = (isReplacing, replaceEverywhere) => {
    if (!isReplacing) {
        return null;
    }
    return replaceEverywhere ? ReplaceMode.everywhere : ReplaceMode.current;
};
const SpeakerDialogTabs = ({ mode, activeTab, onTabChange, replacedSpeaker, onClose, languages, onSubmit, selectForm, editForm, createForm, setSubmitState, replaceEverywhere, isReplacedWithCitation, isCaptionMode, defaultSpeakerColor, getEditorSpeaker, editedId, onEditedIdChange, }) => {
    var _a, _b;
    const { session } = useSession();
    const isSpeakerIdEnabled = session.login.hasClaim('speakerId:enabled');
    const isReplacing = replacedSpeaker !== undefined;
    const selectTabRef = useRef(null);
    const [replaceMode, setReplaceMode] = useState(getInitialReplaceMode(isReplacing, replaceEverywhere));
    const [isCitation, setIsCitation] = useState(isReplacedWithCitation !== null && isReplacedWithCitation !== void 0 ? isReplacedWithCitation : false);
    const [tabChangeData, setTabChangeData] = useState({ id: editedId });
    useEffect(() => {
        if (isCitation) {
            setReplaceMode(ReplaceMode.current);
        }
    }, [isCitation]);
    useEffect(() => {
        if (editedId !== undefined) {
            setTabChangeData((prev) => (Object.assign(Object.assign({}, prev), { id: editedId })));
        }
    }, [editedId]);
    useEffect(() => {
        if (activeTab === 'select') {
            setTimeout(() => { var _a; return (_a = selectTabRef.current) === null || _a === void 0 ? void 0 : _a.focusSearchInput(); }, 0);
        }
    }, [activeTab]);
    const changeTabAndSetData = (key, data) => {
        setTabChangeData(data);
        onTabChange(key);
    };
    const handleSubmit = useCallback((speaker, oldSpeaker, forceReplaceMode) => {
        var _a;
        const isEditTab = activeTab === 'edit';
        const replace = forceReplaceMode !== undefined ? forceReplaceMode : replaceMode;
        const speakerToReplace = oldSpeaker !== undefined ? oldSpeaker
            : ((_a = replacedSpeaker === null || replacedSpeaker === void 0 ? void 0 : replacedSpeaker.editorSpeaker) !== null && _a !== void 0 ? _a : null);
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(speaker, speakerToReplace, replace === ReplaceMode.everywhere, isEditTab ? false : isCitation);
    }, [
        onSubmit,
        replaceMode,
        isCitation,
        activeTab,
    ]);
    const sharedControls = mode === 'select'
        && (isReplacing || isSpeakerIdEnabled) && (_jsxs("div", { className: styles.sharedControls, children: [isReplacing && (_jsx(ReplaceModeRadio, { value: replaceMode, onChange: (value) => {
                    setReplaceMode(value);
                    setTimeout(() => { var _a; return (_a = selectTabRef.current) === null || _a === void 0 ? void 0 : _a.focusSearchInput(); }, 0);
                }, disabled: isCitation, tooltip: isCitation ? txt('replaceModeDisabledByCitation') : undefined })), _jsx(Citation, { value: isCitation, onChange: setIsCitation, label: !isReplacing ? txt('addCitation') : undefined })] }));
    const selectTab = onSubmit !== undefined && mode === 'select'
        ? {
            key: 'select',
            label: txt('select'),
            children: (_jsxs(_Fragment, { children: [sharedControls, _jsx(SelectTab, { ref: selectTabRef, form: selectForm, onChangeTab: changeTabAndSetData, onClose: onClose, onSubmit: handleSubmit, defaultSpeakerColor: defaultSpeakerColor })] })),
            icon: _jsx(SearchOutlined, {}),
        }
        : null;
    const editTab = {
        key: 'edit',
        label: txt('editVerb'),
        children: (_jsx(EditTab, { form: editForm, mode: mode, replacedSpeaker: replacedSpeaker, language: Array.isArray(languages)
                ? ((_a = tabChangeData === null || tabChangeData === void 0 ? void 0 : tabChangeData.language) !== null && _a !== void 0 ? _a : null)
                : languages, onSubmit: handleSubmit, onClose: onClose, editedId: (_b = tabChangeData === null || tabChangeData === void 0 ? void 0 : tabChangeData.id) !== null && _b !== void 0 ? _b : editedId, editedFirstName: tabChangeData === null || tabChangeData === void 0 ? void 0 : tabChangeData.firstName, editedLastName: tabChangeData === null || tabChangeData === void 0 ? void 0 : tabChangeData.lastName, openAddRoleModal: tabChangeData === null || tabChangeData === void 0 ? void 0 : tabChangeData.openAddRoleModal, setTabChangeData: setTabChangeData, getEditorSpeaker: getEditorSpeaker, isCaptionMode: isCaptionMode, defaultSpeakerColor: defaultSpeakerColor, setSubmitState: setSubmitState })),
        icon: _jsx(EditOutlined, {}),
    };
    const createTab = {
        key: 'create',
        label: txt('create'),
        children: (_jsxs(_Fragment, { children: [sharedControls, _jsx(CreateTab, { form: createForm, mode: mode, searchText: tabChangeData === null || tabChangeData === void 0 ? void 0 : tabChangeData.searchText, onChangeTab: changeTabAndSetData, languages: languages, onSubmit: handleSubmit, setSubmitState: setSubmitState, onClose: onClose, isCaptionMode: isCaptionMode, defaultSpeakerColor: defaultSpeakerColor, onEditRedirect: onEditedIdChange })] })),
        icon: _jsx(PlusOutlined, {}),
    };
    const tabs = useMemo(() => {
        switch (mode) {
            case 'edit':
                return [editTab].filter(Boolean);
            case 'create':
                return [createTab].filter(Boolean);
            case 'select':
            default:
                return [selectTab, editTab, createTab].filter(Boolean);
        }
    }, [mode, selectTab, editTab, createTab]);
    if (tabs !== undefined && tabs.length === 1) {
        return (_jsx("div", { className: styles.form, children: _jsx("div", { className: styles.tabContent, children: tabs[0].children }) }));
    }
    return (_jsx("div", { className: styles.form, children: _jsx(Tabs, { activeKey: activeTab, items: tabs, onChange: onTabChange }) }));
};
export default SpeakerDialogTabs;
